import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  X,
  Search,
  Package,
  MapPin,
  Phone,
  ChevronDown,
  ChevronUp,
  Store,
  Tag,
  FileText,
  Loader2,
  PackageOpen,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#E6B43C]/15 text-[#B8860B] border border-[#E6B43C]/30">
      <span className="w-1.5 h-1.5 rounded-full bg-[#E6B43C] animate-pulse" />
      En Fast Max
    </span>
  );
}

// ─── Delivery type badge ───────────────────────────────────────────────────────
function DeliveryTypeBadge({ type }) {
  const isProvince = type === 'provincia';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        isProvince
          ? 'bg-purple-50 text-purple-700 border border-purple-200'
          : 'bg-blue-50 text-blue-700 border border-blue-200'
      }`}
    >
      {isProvince ? 'Provincia' : 'Lima'}
    </span>
  );
}

// ─── Single package card ───────────────────────────────────────────────────────
function PackageCard({ pkg }) {
  const [expanded, setExpanded] = useState(false);
  const hasItems = pkg.items && pkg.items.length > 0;
  const hasNotes = pkg.notes_driver && pkg.notes_driver.trim();

  return (
    <div className="bg-white rounded-xl border border-slate-200 hover:border-[#B1336E]/30 hover:shadow-md transition-all duration-200 overflow-hidden">
      {/* Main row */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Left: IDs + badges */}
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                #{pkg.delivery_point_id}
              </span>
              <DeliveryTypeBadge type={pkg.delivery_type} />
              <StatusBadge />
            </div>

            {/* Recipient */}
            {pkg.recipient_name && (
              <p className="text-sm font-semibold text-slate-800 truncate">
                {pkg.recipient_name}
              </p>
            )}

            <div className="flex flex-wrap gap-3 mt-0.5">
              {pkg.phone_number && (
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <Phone className="w-3 h-3" />
                  {pkg.phone_number}
                </span>
              )}
              {pkg.district && (
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <MapPin className="w-3 h-3" />
                  {pkg.district}
                </span>
              )}
            </div>
          </div>

          {/* Right: expand toggle */}
          {(hasItems || hasNotes) && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-[#B1336E] hover:bg-[#B1336E]/5 transition-colors"
            >
              {expanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          )}
        </div>

        {/* Tracking code */}
        <div className="mt-2 flex items-center gap-1">
          <Tag className="w-3 h-3 text-slate-400" />
          <span className="text-xs text-slate-400 font-mono truncate">
            {pkg.tracking_code}
          </span>
        </div>
      </div>

      {/* Expanded section */}
      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60 space-y-3">
          {/* Notes driver */}
          {hasNotes && (
            <div className="flex gap-2">
              <FileText className="w-3.5 h-3.5 text-[#B1336E] mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-medium text-slate-500 mb-0.5">
                  Nota para repartidor
                </p>
                <p className="text-xs text-slate-700 leading-relaxed">
                  {pkg.notes_driver}
                </p>
              </div>
            </div>
          )}

          {/* Items */}
          {hasItems && (
            <div className="flex gap-2">
              <Package className="w-3.5 h-3.5 text-[#B1336E] mt-0.5 shrink-0" />
              <div className="w-full">
                <p className="text-xs font-medium text-slate-500 mb-1">
                  Productos ({pkg.items.length})
                </p>
                <div className="space-y-1">
                  {pkg.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between text-xs bg-white rounded-lg px-2.5 py-1.5 border border-slate-200"
                    >
                      <span className="font-mono text-slate-600 truncate">
                        {item.product_sku || '—'}
                      </span>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        {item.unit_price && (
                          <span className="text-slate-400">
                            S/ {Number(item.unit_price).toFixed(2)}
                          </span>
                        )}
                        <span className="font-semibold text-[#B1336E] bg-[#B1336E]/8 px-1.5 py-0.5 rounded">
                          ×{item.quantity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Store group (provider view) ───────────────────────────────────────────────
function StoreGroup({ storeName, packages }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="mb-5">
      {/* Store header */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 mb-2 rounded-lg bg-[#B1336E]/5 hover:bg-[#B1336E]/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-[#B1336E]" />
          <span className="text-sm font-semibold text-[#B1336E] font-montserrat">
            {storeName}
          </span>
          <span className="text-xs text-slate-500 bg-white border border-slate-200 px-1.5 py-0.5 rounded-full">
            {packages.length} paq.
          </span>
        </div>
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-[#B1336E]" />
        ) : (
          <ChevronUp className="w-4 h-4 text-[#B1336E]" />
        )}
      </button>

      {!collapsed && (
        <div className="space-y-2.5 pl-1">
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ isFiltered }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <PackageOpen className="w-8 h-8 text-slate-400" />
      </div>
      <p className="text-sm font-semibold text-slate-600">
        {isFiltered ? 'Sin resultados' : 'Sin paquetes en Fast Max'}
      </p>
      <p className="text-xs text-slate-400 mt-1">
        {isFiltered
          ? 'Intenta con otro nombre, teléfono o ID'
          : 'Los paquetes aparecerán aquí cuando estén en depósito Fast Max'}
      </p>
    </div>
  );
}

// ─── Main modal component ──────────────────────────────────────────────────────
export function PackagesInFastmax({ isOpen, onClose, userId }) {
  const [packages, setPackages] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  const fetchPackages = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/packages/in-fastmax`, {
        params: { user_id: userId },
      });
      setPackages(data.packages || []);
      setUserRole(data.role);
    } catch (err) {
      console.error('Error fetching packages in fastmax:', err);
      setError('No se pudo cargar la lista. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Fetch on open
  useEffect(() => {
    if (isOpen) {
      fetchPackages();
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 150);
    }
  }, [isOpen, fetchPackages]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // ── Filter logic ──────────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const filtered = q
    ? packages.filter(
        (p) =>
          String(p.delivery_point_id).includes(q) ||
          (p.recipient_name && p.recipient_name.toLowerCase().includes(q)) ||
          (p.phone_number && p.phone_number.includes(q))
      )
    : packages;

  const isProvider = userRole === 'provider';
  const isFiltered = q.length > 0;

  // ── Group by store for provider ───────────────────────────────────────────
  const grouped = isProvider
    ? filtered.reduce((acc, pkg) => {
        const key = pkg.store_name || 'Sin tienda';
        if (!acc[key]) acc[key] = [];
        acc[key].push(pkg);
        return acc;
      }, {})
    : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[88vh] animate-in slide-in-from-bottom-4 duration-300">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#B1336E]/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-[#B1336E]" />
              </div>
              <div>
                <h2 className="text-base font-extrabold text-slate-800 font-montserrat leading-tight">
                  Paquetes en Fast Max
                </h2>
                <p className="text-xs text-slate-400">
                  {loading ? 'Cargando...' : `${filtered.length} de ${packages.length} paquetes`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchPackages}
                disabled={loading}
                className="p-2 text-slate-400 hover:text-[#B1336E] hover:bg-[#B1336E]/5 rounded-lg transition-colors disabled:opacity-40"
                title="Actualizar"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search bar */}
          <div className="px-5 py-3 border-b border-slate-100 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por ID, destinatario o teléfono..."
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/30 focus:border-[#B1336E] transition-all placeholder:text-slate-400 font-montserrat"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {/* Loading */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-8 h-8 text-[#B1336E] animate-spin" />
                <p className="text-sm text-slate-500">Cargando paquetes...</p>
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <p className="text-sm text-red-500">{error}</p>
                <button
                  onClick={fetchPackages}
                  className="text-xs text-[#B1336E] underline hover:no-underline"
                >
                  Reintentar
                </button>
              </div>
            )}

            {/* Content */}
            {!loading && !error && (
              <>
                {filtered.length === 0 ? (
                  <EmptyState isFiltered={isFiltered} />
                ) : isProvider ? (
                  // Provider: grouped by store
                  Object.entries(grouped).map(([storeName, pkgs]) => (
                    <StoreGroup
                      key={storeName}
                      storeName={storeName}
                      packages={pkgs}
                    />
                  ))
                ) : (
                  // Customer: flat list
                  <div className="space-y-2.5">
                    {filtered.map((pkg) => (
                      <PackageCard key={pkg.id} pkg={pkg} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer summary */}
          {!loading && !error && packages.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100 shrink-0 bg-slate-50/60 rounded-b-2xl">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>
                  {packages.filter((p) => p.delivery_type === 'lima').length} Lima
                  {' · '}
                  {packages.filter((p) => p.delivery_type === 'provincia').length} Provincia
                </span>
                <span className="font-semibold text-slate-700">
                  Total: {packages.length} paquetes
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}